import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface BenefitPickerListProps {
  allPrestaciones: any[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  filterByActive?: boolean;
  loading?: boolean;
}

const isActive = (pres: any): boolean => {
  const val = pres.Activa;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'string') return val.toLowerCase() === 'activo';
  return false;
};

const BenefitPickerList = ({ allPrestaciones, selectedIds, onChange, filterByActive = true, loading = false }: BenefitPickerListProps) => {
  // Siempre se muestran las activas; si hay seleccionadas inactivas también se muestran para que puedan gestionarse
  const displayList = filterByActive
    ? allPrestaciones.filter(p => isActive(p) || selectedIds.includes(String(p.id)))
    : allPrestaciones;
  const displayIds = displayList.map((p) => String(p.id));

  const allChecked = displayIds.length > 0 && displayIds.every((id) => selectedIds.includes(id));
  const someChecked = displayIds.some((id) => selectedIds.includes(id)) && !allChecked;

  const handleSelectAll = () => {
    if (allChecked) {
      onChange(selectedIds.filter((id) => !displayIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selectedIds, ...displayIds]));
      onChange(merged);
    }
  };

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <CircularProgress size={24} style={{ color: '#FABB00' }} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Select all con estado indeterminate */}
      <FormControlLabel
        label={
          <Typography style={{ fontFamily: 'Roboto', fontSize: 13, fontWeight: 500 }}>
            Seleccionar todas ({selectedIds.filter((id) => displayIds.includes(id)).length}/{displayIds.length})
          </Typography>
        }
        control={
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={someChecked}
            onChange={handleSelectAll}
            disabled={displayIds.length === 0}
            sx={{
              color: '#FABB00',
              '&.Mui-checked': { color: '#FABB00' },
              '&.MuiCheckbox-indeterminate': { color: '#FABB00' },
            }}
          />
        }
      />
      <Divider style={{ marginBottom: 4 }} />

      {/* Lista de prestaciones */}
      <div style={{
        maxHeight: 220,
        overflowY: 'auto',
        padding: '4px 8px',
        border: '1px solid #c4c4c4',
        borderRadius: 4,
      }}>
        {allPrestaciones.length === 0 && (
          <Typography variant="body2" style={{ color: '#999', padding: 8 }}>
            No hay prestaciones disponibles
          </Typography>
        )}
        {displayList.map((pres: any) => (
          <FormControlLabel
            key={pres.id}
            style={{ margin: 0, width: '100%' }}
            label={
              <Tooltip
                title={pres.TipoPrestacion ?? ''}
                placement="right"
                arrow
                disableHoverListener={!pres.TipoPrestacion}
              >
                <span style={{ fontFamily: 'Roboto', fontSize: 13, fontWeight: 500 }}>
                  {pres.Titulo}
                </span>
              </Tooltip>
            }
            control={
              <Checkbox
                size="small"
                checked={selectedIds.includes(String(pres.id))}
                onChange={() => toggle(String(pres.id))}
                sx={{
                  color: '#FABB00',
                  '&.Mui-checked': { color: '#FABB00' },
                  padding: '4px 8px',
                }}
              />
            }
          />
        ))}
      </div>
    </div>
  );
};

export default BenefitPickerList;
